import { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  MenuItem,
  Pagination,
  Stack,
  TableBody,
  TextField,
  Typography,
} from "@mui/material";
import { ExpandMore as ExpandIcon } from "@mui/icons-material";
import EstadoChip from "../../components/kit/EstadoChip";
import Etiqueta from "../../components/kit/Etiqueta";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import { useCorreos, useOpcionesCorreos } from "../../queries/useCorreos";
import { CorreoEnviado, FiltrosCorreos } from "../../types/correo";
import { Accesores, ordenar, useOrden } from "../../utils/orden";
import { formatFechaHora } from "../../utils/fecha";

const COLUMNAS: (string | Columna)[] = [
  { label: "Asunto", sortKey: "asunto" },
  { label: "Para", sortKey: "para" },
  { label: "De", sortKey: "de" },
  { label: "Paso y período", sortKey: "paso" },
  { label: "Estado", sortKey: "estado" },
  { label: "Fecha", width: 150, sortKey: "fecha" },
];

const ACC: Accesores<CorreoEnviado> = {
  asunto: (c) => c.asunto,
  // Por el nombre si lo hay; si la persona ya no está, por la dirección a la que se
  // envió, que es lo que la fila muestra en su lugar.
  para: (c) => c.destinatario || c.email,
  de: (c) => c.remitente,
  paso: (c) => `${c.paso?.nombre ?? ""} ${c.periodo?.periodo ?? ""}`,
  // Los fallidos primero en ascendente: son los que hay que mirar.
  estado: (c) => (c.enviado ? 1 : 0),
  fecha: (c) => c.fecha_creacion,
};

/**
 * El orden es de la página que se está mirando, no de la bitácora entera: el listado
 * viene paginado del servidor, así que ordenar reacomoda estas filas y no sale a
 * buscar las de las otras páginas.
 */

/**
 * "Correos enviados": la bitácora del sistema, completa y de solo lectura.
 *
 * No filtra por quien mira: la pregunta que responde es "¿qué se le escribió a
 * esta cohorte y quién lo mandó?", y para eso una lista recortada al remitente
 * sirve de poco. Incluye los avisos automáticos, no solo los que alguien escribió.
 *
 * Los envíos fallidos quedan a la vista con su error: es la razón de que esto sea
 * una tabla y no el log del servidor.
 */
export default function CorreosEnviados() {
  const [filtros, setFiltros] = useState<FiltrosCorreos>({ page: 1 });
  const { data, isLoading, isError } = useCorreos(filtros);
  const { data: opciones } = useOpcionesCorreos();

  // Cambiar un filtro vuelve a la primera página: si no, se cae en una vacía.
  const set = (patch: Partial<FiltrosCorreos>) => setFiltros((f) => ({ ...f, ...patch, page: 1 }));

  const correos = data?.results ?? [];
  const { orden, onSort } = useOrden();
  const paginas = Math.ceil((data?.count ?? 0) / 50);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Correos enviados
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Todo lo que salió del sistema, lo haya escrito una persona o lo haya disparado un evento. Se puede mirar, no editar.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Asunto, destinatario o remitente…"
          value={filtros.q ?? ""}
          onChange={(e) => set({ q: e.target.value })}
          sx={{ minWidth: 240 }}
        />
        <TextField select size="small" label="Tipo" value={filtros.tipo ?? ""} onChange={(e) => set({ tipo: e.target.value })} sx={{ minWidth: 190 }}>
          <MenuItem value="">Todos</MenuItem>
          {(opciones?.tipos ?? []).map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Paso" value={filtros.paso ?? ""} onChange={(e) => set({ paso: e.target.value })} sx={{ minWidth: 190 }}>
          <MenuItem value="">Todos</MenuItem>
          {(opciones?.pasos ?? []).map((p) => (
            <MenuItem key={p.id} value={String(p.id)}>
              {p.nombre}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Período"
          value={filtros.periodo ?? ""}
          onChange={(e) => set({ periodo: e.target.value })}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {(opciones?.periodos ?? []).map((p) => (
            <MenuItem key={p.periodo} value={p.periodo}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Estado"
          value={filtros.estado ?? ""}
          onChange={(e) => set({ estado: e.target.value })}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="enviado">Enviados</MenuItem>
          <MenuItem value="fallido">Fallidos</MenuItem>
        </TextField>
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudo cargar la bitácora de correos.</Alert>}

      {!isLoading && !isError && correos.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No hay correos que calcen con esos filtros.
        </Typography>
      )}

      {!isLoading && !isError && correos.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {data!.count} {data!.count === 1 ? "correo" : "correos"}
          </Typography>
          <TablaKit columnas={COLUMNAS} minWidth={980} orden={orden} onSort={onSort}>
            <TableBody>
              {ordenar(correos, ACC, orden).map((c) => (
                <FilaCorreo key={c.id} correo={c} />
              ))}
            </TableBody>
          </TablaKit>

          {paginas > 1 && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={paginas}
                page={filtros.page ?? 1}
                onChange={(_, page) => setFiltros((f) => ({ ...f, page }))}
                size="small"
              />
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

/** Una fila; al hacer clic se abre el cuerpo tal como se envió. */
function FilaCorreo({ correo }: { correo: CorreoEnviado }) {
  const [abierto, setAbierto] = useState(false);

  return (
    <>
      <FilaKit onClick={() => setAbierto((v) => !v)}>
        <CeldaPrincipal maxWidth={320}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <ExpandIcon
              fontSize="small"
              sx={{ color: "text.disabled", transform: abierto ? "rotate(180deg)" : "none", transition: "transform .15s" }}
            />
            <Box sx={{ minWidth: 0 }}>
              {correo.asunto}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 400 }}>
                {correo.tipo_display}
              </Typography>
            </Box>
          </Stack>
        </CeldaPrincipal>
        <CeldaKit>
          {correo.destinatario || "—"}
          {/* La dirección va aparte del nombre: pudo cambiar después del envío. */}
          <Typography variant="caption" color="text.secondary" display="block">
            {correo.email}
          </Typography>
        </CeldaKit>
        <CeldaKit>{correo.remitente || <Box component="span" sx={{ color: "text.disabled" }}>Automático</Box>}</CeldaKit>
        <CeldaKit>
          {correo.paso || correo.periodo ? (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {correo.paso && <Etiqueta>{correo.paso.nombre}</Etiqueta>}
              {correo.periodo && <Etiqueta variante="contorno">{correo.periodo.label}</Etiqueta>}
            </Stack>
          ) : (
            <Box component="span" sx={{ color: "text.disabled" }}>—</Box>
          )}
        </CeldaKit>
        <CeldaKit>
          <EstadoChip
            label={correo.enviado ? "Enviado" : "Falló"}
            tono={correo.enviado ? "exito" : "error"}
            icono={correo.enviado ? "check" : undefined}
            denso
          />
        </CeldaKit>
        <CeldaKit noWrap>{formatFechaHora(correo.fecha_creacion)}</CeldaKit>
      </FilaKit>

      <tr>
        <td colSpan={6} style={{ padding: 0, borderTop: "none" }}>
          <Collapse in={abierto} unmountOnExit>
            <Box sx={{ px: 3, py: 2 }}>
              {correo.error && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {correo.error}
                </Alert>
              )}
              {correo.responder_a && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Las respuestas van a {correo.responder_a}
                </Typography>
              )}
              {/* El cuerpo se guardó en texto plano: se muestra tal cual salió. */}
              <Typography
                variant="body2"
                component="pre"
                sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit", m: 0, color: "text.secondary" }}
              >
                {correo.cuerpo || "(sin cuerpo)"}
              </Typography>
            </Box>
          </Collapse>
        </td>
      </tr>
    </>
  );
}
